import type {
  TextBlockModifierValue,
  TextTagModifierValue,
} from "schemas/generated/fileModifiers";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { stylesRouter } from "@editor/trpc/styles";

import { useDefaultSelectedElementDesignation } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { LargeMenuItem } from "@replo/design-system/components/menu/LargeMenuItem";
import { skipToken } from "@tanstack/react-query";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { convertFontWeightToReadableString } from "replo-runtime/shared/utils/font";

interface SavedStyleSelectorProps {
  onChange: (modifiers: TextBlockModifierValue[]) => void;
  onExitSavedStyleMode: () => void;
  styleSelectorTrigger: (triggerData: {
    typography: Array<{
      name: string;
      key: string;
      htmlTag: string;
      settings: any;
    }>;
    displayName: string | null;
    onExitSavedStyleMode: () => void;
  }) => React.ReactNode;
}

const SavedStyleSelector: React.FC<SavedStyleSelectorProps> = ({
  onChange,
  onExitSavedStyleMode,
  styleSelectorTrigger,
}) => {
  const projectId = useCurrentProjectId();
  const defaultDesignation = useDefaultSelectedElementDesignation();

  const { data: styleLibraryData } = stylesRouter.getStyleLibrary.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const currentStyleLibrary = styleLibraryData?.styleLibrary ?? null;

  const typography = React.useMemo(() => {
    if (!currentStyleLibrary?.typography) {
      return [];
    }

    return [
      {
        name: "Heading 1",
        key: "h1",
        htmlTag: "h1" as const,
        settings: currentStyleLibrary.typography.h1,
      },
      {
        name: "Heading 2",
        key: "h2",
        htmlTag: "h2" as const,
        settings: currentStyleLibrary.typography.h2,
      },
      {
        name: "Heading 3",
        key: "h3",
        htmlTag: "h3" as const,
        settings: currentStyleLibrary.typography.h3,
      },
      {
        name: "Heading 4",
        key: "h4",
        htmlTag: "h4" as const,
        settings: currentStyleLibrary.typography.h4,
      },
      {
        name: "Paragraph",
        key: "p",
        htmlTag: "p" as const,
        settings: currentStyleLibrary.typography.body,
      },
    ];
  }, [currentStyleLibrary?.typography]);

  const currentStyleKey = defaultDesignation?.element.tagName
    ? typography.find(
        (style) => style.htmlTag === defaultDesignation.element.tagName,
      )?.key ?? null
    : null;

  const handleStyleChange = (styleKey: string | null) => {
    const style = styleKey ? typography.find((t) => t.key === styleKey) : null;

    if (style) {
      const modifier: TextTagModifierValue = {
        type: "textTag",
        value: style.htmlTag,
      };
      onChange([modifier]);
    }
  };

  const [value, setValue] = useOverridableState(
    currentStyleKey,
    handleStyleChange,
  );

  // NOTE (Kurt, 2025-07-29): Look up the display name for the currently selected typography style
  // by finding the style object that matches the current value (style key)
  const displayName = value
    ? typography.find((style) => style.key === value)?.name ?? null
    : null;

  // NOTE (Kurt, 2025-07-29): Transform typography styles into combobox options with formatted display labels that show
  // the style name, font details, and HTML tag indicator.
  const styleOptions = typography.map((style) => ({
    value: style.key,
    displayValue: style.name,
    label: (
      <LargeMenuItem
        variant="flexible"
        label={style.name}
        description={[
          style.settings.name,
          style.settings.size ? `${style.settings.size}px` : undefined,
          style.settings.weight
            ? convertFontWeightToReadableString(
                style.settings.weight.toString(),
              )
            : undefined,
        ]
          .filter(Boolean)
          .join(" \u00B7 ")}
        startEnhancer={
          <div className="text-muted border-border border-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-base font-medium">
            {style.htmlTag.toUpperCase()}
          </div>
        }
      />
    ),
    estimatedSize: 44,
  }));

  return (
    <div className="overflow-hidden">
      <Combobox.Root
        options={styleOptions}
        value={value ?? undefined}
        onChange={setValue}
      >
        <Combobox.Trigger>
          {styleSelectorTrigger({
            typography,
            displayName,
            onExitSavedStyleMode,
          })}
        </Combobox.Trigger>
        <Combobox.Popover
          sideOffset={8}
          side="bottom"
          // NOTE (Kurt, 2025-07-29): We add this so that when the combobox trigger is a small button
          // instead of the default combobox select, the content should not collapse to
          // the width of the trigger, it should be at least 72px wide.
          unsafe_className="!min-w-72 py-0.5"
        >
          <Combobox.Content areOptionsSearchable />
        </Combobox.Popover>
      </Combobox.Root>
    </div>
  );
};

export default SavedStyleSelector;
